import {
  attachClosestEdge,
  Edge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { RefObject } from 'preact';
import { useEffect, useState } from 'preact/hooks';

export { type Edge };

// Setup draggable handle and also make the card a drop target
export function useDraggableHandle({
  cardRef,
  handleRef,
  id,
  onDrop,
}: {
  cardRef: RefObject<HTMLElement>;
  handleRef: RefObject<HTMLElement>;
  id: string;
  onDrop: (draggedId: string, targetId: string, edge: Edge) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null); // "top" | "bottom" | null

  useEffect(() => {
    const card = cardRef.current;
    const handle = handleRef.current;
    if (!card || !handle) return;

    return combine(
      draggable({
        element: card,
        dragHandle: handle,
        getInitialData: () => ({ id }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: card,
        getData: ({ input, element }) =>
          attachClosestEdge(
            { id },
            { input, element, allowedEdges: ['top', 'bottom'] }
          ),
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: ({ self, source }) => {
          setClosestEdge(null);
          const edge = extractClosestEdge(self.data);
          const draggedId = source.data.id as string;
          if (!edge || !draggedId) return;
          onDrop(draggedId, id, edge);
        },
      })
    );
  }, [id]);

  return { isDragging, closestEdge };
}

// ─── reorder helper ────────────────────────────────────────────────────────

export function reorder<T extends { id: string }>(
  items: T[],
  draggedId: string,
  targetId: string,
  edge: Edge
) {
  if (draggedId === targetId) return items;
  const next = [...items];
  const fromIdx = next.findIndex((i) => i.id === draggedId);
  const [moved] = next.splice(fromIdx, 1);
  const toIdx = next.findIndex((i) => i.id === targetId);
  next.splice(edge === 'bottom' ? toIdx + 1 : toIdx, 0, moved);
  return next;
}

// // ─── Example usage ─────────────────────────────────────────────────────────

// function Card({ item, onDrop }) {
//   const cardRef = useRef<HTMLDivElement>(null);
//   const handleRef = useRef<HTMLDivElement>(null);

//   const { isDragging, closestEdge } = useDraggableHandle({
//     cardRef,
//     handleRef,
//     id: item.id,
//     onDrop,
//   });

//   return (
//     <div class="relative">
//       {/* drop indicators */}
//       {closestEdge === 'top' && (
//         <div class="absolute inset-x-0 -top-px h-0.5 rounded bg-blue-500" />
//       )}
//       {closestEdge === 'bottom' && (
//         <div class="absolute inset-x-0 -bottom-px h-0.5 rounded bg-blue-500" />
//       )}

//       <div
//         ref={cardRef}
//         class={`flex items-center gap-3 border border-gray-200 bg-white px-4 py-3 transition-opacity ${isDragging ? 'opacity-40' : 'opacity-100'}`}
//       >
//         <div
//           ref={handleRef}
//           class="cursor-grab touch-none rounded p-1 text-gray-300 hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing"
//         >
//           Handle
//         </div>
//         <span class="text-sm text-gray-800">{item.label}</span>
//       </div>
//     </div>
//   );
// }

// const INITIAL_ITEMS = [
//   { id: '1', label: 'First item' },
//   { id: '2', label: 'Second item' },
//   { id: '3', label: 'Third item' },
//   { id: '4', label: 'Fourth item' },
// ];

// export default function SortableList() {
//   const [items, setItems] = useState(INITIAL_ITEMS);

//   const handleDrop = (draggedId, targetId, edge) =>
//     setItems((prev) => reorder(prev, draggedId, targetId, edge));

//   return (
//     <div class="flex w-80 flex-col divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
//       {items.map((item) => (
//         <Card key={item.id} item={item} onDrop={handleDrop} />
//       ))}
//     </div>
//   );
// }
